import type {
  FetchResult,
  NextLink,
  Observable,
  Operation,
} from '@apollo/client/core';
import { ApolloLink } from '@apollo/client/core';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { auth, hasAccessTokenExpired } from '@frameio/utils/auth';
import { isValidIntegrationsRedirectPath } from '@frameio/utils/validators/isValidIntegrationsRedirectPath';
import { Authorization, Me } from '../../generated';
import { getOperationNameFromDocumentNode } from '../../utils/getOperationNameFromDocumentNode';
import { OperationQueue } from './AuthRefreshLink/OperationQueue';
import { handleUXPRefresh } from './handleUXPRefresh';

let isCurrentlyRefreshingAuthToken = false;

/**
 * We allow all operations with `resolveWithUXP` in the context to pass through
 * UXPRefreshLink without being enqueued. This array is a list of actual
 * Massdriver backend operations we want to allow through.
 */
const ALLOWED_OPERATIONS = [
  getOperationNameFromDocumentNode(Me),
  getOperationNameFromDocumentNode(Authorization),
] as const;

/**
 * UXPRefreshLink is analogous to AuthRefreshLink but is tailored for
 * environments running inside an Adobe UXP host. When the access token is
 * expired it will use the host messaging layer (uxpHost.postMessage) to
 * execute the `refreshAccessToken` GraphQL mutation and queue incoming
 * operations until the token refresh completes.
 */
export class UXPRefreshLink extends ApolloLink {
  private queue: OperationQueue;

  constructor() {
    super();
    this.queue = new OperationQueue();
  }

  private handleBrowserOnline = async (): Promise<void> => {
    window.removeEventListener('online', this.handleBrowserOnline);

    if (!hasAccessTokenExpired(auth.getAccessToken())) {
      this.queue.consumeQueue();
    } else {
      await this.fetchAccessTokenThenConsumeQueue();
    }
  };

  /**
   * On a failed refresh attempt, redirect to the root / path to resolve authentication.
   */
  private handleFailedRefresh = (): void => {
    const redirectParam = isValidIntegrationsRedirectPath(
      window.location.pathname
    )
      ? `?redirectPath=${window.location.pathname}`
      : '';
    window.location.assign(`/${redirectParam}`);
  };

  private async fetchAccessTokenThenConsumeQueue(): Promise<void> {
    isCurrentlyRefreshingAuthToken = true;

    try {
      const success = await handleUXPRefresh();
      if (success) {
        this.queue.consumeQueue();
      } else {
        this.handleFailedRefresh();
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      datadogLogger.warn('uxp_failure_to_refresh_token', {
        location: 'UXPRefreshLink',
        error: errorMessage,
      });
      this.handleFailedRefresh();
    }

    isCurrentlyRefreshingAuthToken = false;
  }

  private shouldBypassTokenCheck(operation: Operation): boolean {
    const operationName = operation.operationName;
    const hasResolveWithUXPInContext = operation.getContext().resolveWithUXP;
    return (
      hasResolveWithUXPInContext || ALLOWED_OPERATIONS.includes(operationName)
    );
  }

  public request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> | null {
    if (typeof forward !== 'function') {
      throw new Error(
        'UXPRefreshLink is a non-terminating link and should not be last in the composed chain'
      );
    }

    const shouldBypassTokenCheck = this.shouldBypassTokenCheck(operation);

    if (!shouldBypassTokenCheck && !window?.navigator?.onLine) {
      window.addEventListener('online', this.handleBrowserOnline);
      return this.queue.enqueueRequest({
        operation,
        forward,
      });
    }

    /**
     * We need to allow auth related and server operations to bypass being enqueued
     * in an unauthenticated or refreshing state.
     */
    if (shouldBypassTokenCheck) {
      return forward(operation);
    }

    const hasTokenExpired = hasAccessTokenExpired(auth.getAccessToken());

    if (!hasTokenExpired) {
      return forward(operation);
    }

    if (!isCurrentlyRefreshingAuthToken) {
      this.fetchAccessTokenThenConsumeQueue();
    }

    // eslint-disable-next-line no-console
    console.log('enqueueing operation', operation.operationName);
    return this.queue.enqueueRequest({
      operation,
      forward,
    });
  }
}

const uxpRefreshLink = (): UXPRefreshLink => {
  return new UXPRefreshLink();
};

export default uxpRefreshLink;
